import { useRef, useState, useEffect } from 'react'
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs'
import { RootState } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from "react-router";
import PurchaseTypeDropdown from '../../MNOManagament/components/PurchaseTypeDropdown';
import AddModel from '../../../components/Add/AddModel';
import AddBottomButton from '../../../components/Add/AddBottomButton';
import useApi from '../../../services/useApi';
import { showToast } from '../../../components/toastMessage/toastSlice';
import { resetManualState } from '../../../store/slices/addManualSlice';

export default function AddManualTransactions() {
  const [errors, setErrors] = useState<{
    purchase_details?: {
      source_accounts?: Array<{
        source_account_name?: string;
        purchased_amount?: string;
        receipt_url?: string;
      }>;
      destination_accounts?: Array<{
        destination_account_name?: string;
        purchased_amount?: string;
        receipt_url?: string;
      }>;
    }
  }>({});
  const sourceRefs = useRef<(HTMLDivElement | null)[]>([]);
  const destinationRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { getAPI, PostAPI } = useApi();
  const [baseAccounts, setBaseAccounts] = useState<Record<string, string>[]>([]);
  const [sourceAccounts, setSourceAccounts] = useState<Record<string, string>[]>([]);
  const [destinationAccounts, setDestinationAccounts] = useState<Record<string, string>[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const selectedBankIdState = useSelector((state: RootState) => state.selectedBankId);
  const { listOfBankAccountsUrl, listOfBankTransactionsUrl, listOfBalanceSheetUrl } = useSelector((state: RootState) => state.selectedBankId);
  const { sources, destinations } = useSelector((state: RootState) => state.addManual);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isFromJournalVoucher = location.pathname.includes('/journal-voucher');
  const [hasMoreSource, setHasMoreSource] = useState(true);
  const [hasMoreDestination, setHasMoreDestination] = useState(true);
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [sourceSearchPage, setSourceSearchPage] = useState(1);
  const [destinationSearchTerm, setDestinationSearchTerm] = useState('');
  const [destinationSearchPage, setDestinationSearchPage] = useState(1);
  const [justSearchedSource, setJustSearchedSource] = useState(false);
  const [justSearchedDestination, setJustSearchedDestination] = useState(false);

  // Handler for loading more source accounts
  const onLoadMoreOptionsSource = async (searchTerm: string) => {
    const isNewSearch = searchTerm !== sourceSearchTerm;

    if (!isNewSearch && !hasMoreSource) {
      // If not a new search and no more results, don't fetch
      return;
    }
    setAccountsLoading(true);

    const nextPage = isNewSearch ? 1 : sourceSearchPage + 1;

    if (isNewSearch) {
      setSourceSearchTerm(searchTerm);
      setHasMoreSource(true);
      setJustSearchedSource(true);
    } else {
      if (justSearchedSource) {
        setJustSearchedSource(false);
        setAccountsLoading(false);
        return;
      }
    }

    // Always call the API with the current searchTerm
    const response = await getAPI(`manual-entries/accounts?type=journal-voucher&page_number=${nextPage}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`);
    let fetchedAccounts = response?.data?.data || [];

    if (fetchedAccounts.length < 50) {
      setHasMoreSource(false);
    }

    fetchedAccounts = fetchedAccounts.map((acc: Record<string, string>) =>
      acc.group_type === 'Bank Charges'
        ? { ...acc, name: acc.name + ' (Bank Charges)' }
        : acc
    );

    setBaseAccounts(prev => {
      let merged;
      if (nextPage === 1) {
        merged = fetchedAccounts;
      } else {
        merged = [...prev, ...fetchedAccounts.filter((acc: Record<string, string>) => !prev.some(a => a.id === acc.id))];
      }
      setSourceAccounts(merged.filter((acc: any) => !sources.map((s: any) => s.source_account_id).includes(acc.id)));
      return merged;
    });

    setSourceSearchPage(nextPage);
    setAccountsLoading(false);
  };

  // Handler for loading more destination accounts
  const onLoadMoreOptionsDestination = async (searchTerm: string) => {
    const isNewSearch = searchTerm !== destinationSearchTerm;

    if (!isNewSearch && !hasMoreDestination) {
      // If not a new search and no more results, don't fetch
      return;
    }

    setAccountsLoading(true);

    const nextPage = isNewSearch ? 1 : destinationSearchPage + 1;

    if (isNewSearch) {
      setDestinationSearchTerm(searchTerm);
      setHasMoreDestination(true);
      setJustSearchedDestination(true);
    } else {
      if (justSearchedDestination) {
        setJustSearchedDestination(false);
        setAccountsLoading(false);
        return;
      }
    }

    // Always call the API with the current searchTerm
    const response = await getAPI(`manual-entries/accounts?type=journal-voucher&page_number=${nextPage}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`);
    let fetchedAccounts = response?.data?.data || [];

    if (fetchedAccounts.length < 50) {
      setHasMoreDestination(false);
    }

    fetchedAccounts = fetchedAccounts.map((acc: Record<string, string>) =>
      acc.group_type === 'Bank Charges'
        ? { ...acc, name: acc.name + ' (Bank Charges)' }
        : acc
    );

    setBaseAccounts(prev => {
      let merged;
      if (nextPage === 1) {
        merged = fetchedAccounts;
      } else {
        merged = [...prev, ...fetchedAccounts.filter((acc: Record<string, string>) => !prev.some(a => a.id === acc.id))];
      }
      setDestinationAccounts(merged.filter((acc: any) => !destinations.map((d: any) => d.destination_account_id).includes(acc.id)));
      return merged;
    });

    setDestinationSearchPage(nextPage);
    setAccountsLoading(false);
  };

  useEffect(() => {
    return () => {
      dispatch(resetManualState());
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      const response = await getAPI('manual-entries/accounts?type=journal-voucher&page_number=1');
      const fetchedAccounts = response?.data?.data || [];
      setBaseAccounts(fetchedAccounts);
      setSourceAccounts(fetchedAccounts);
      setDestinationAccounts(fetchedAccounts);
      setAccountsLoading(false);
    };
    fetchAccounts();
  }, []);

  useEffect(() => {
    const selectedSourceIds = new Set(sources.map((s: any) => s.source_account_id));
    const selectedDestinationIds = new Set(destinations.map((d: any) => d.destination_account_id));

    setDestinationAccounts(baseAccounts.filter((acc: any) => !selectedSourceIds.has(acc.id)));
    setSourceAccounts(baseAccounts.filter((acc: any) => !selectedDestinationIds.has(acc.id)));
  }, [sources, destinations, baseAccounts]);

  const handleSubmit = async () => {
    const newErrors = {
      purchase_details: {
        source_accounts: sources.map((source: any) => {
          if (!source.source_account_id) {
            return {
              source_account_name: "This field is required",
              purchased_amount: "",
              note: "",
            };
          }
          return {
            source_account_name: "",
            purchased_amount: !source.purchased_amount ? "This field is required" : Number(source.purchased_amount) === 0 ? "Amount must be greater than zero" : "",
            note: !source.notes ? "This field is required" : "",
          };
        }),
        destination_accounts: destinations.map((destination: any) => {
          if (destination.destination_account_name === "") {
            return {
              destination_account_name: "This field is required",
              purchased_amount: "",
              note: "",
            };
          }
          return {
            destination_account_name: "",
            purchased_amount: !destination.purchased_amount ? "This field is required" : Number(destination.purchased_amount) === 0 ? "Amount must be greater than zero" : "",
            note: !destination.notes ? "This field is required" : "",
          };
        }),
      }
    };
    setErrors(newErrors);

    const hasSourceErrors = newErrors.purchase_details.source_accounts.some((error: any) => Object.values(error).some(value => value !== ""));
    const hasDestinationErrors = newErrors.purchase_details.destination_accounts.some((error: any) => Object.values(error).some(value => value !== ""));

    if (hasSourceErrors || hasDestinationErrors) {
      // Find the first error index in source_accounts
      let errorIndex = newErrors.purchase_details.source_accounts.findIndex(
        (error: any) => Object.values(error).some(value => value !== "")
      );
      let refToScroll = sourceRefs.current[errorIndex];

      // If not found in source, check destination_accounts
      if (errorIndex === -1) {
        errorIndex = newErrors.purchase_details.destination_accounts.findIndex(
          (error: any) => Object.values(error).some(value => value !== "")
        );
        refToScroll = destinationRefs.current[errorIndex];
      }

      // Scroll to the error element using ref
      if (errorIndex !== -1 && refToScroll) {
        refToScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const totalSourceAmount = sources.reduce((total: number, source: any) => total + parseFloat(source.purchased_amount || 0), 0);
    const totalDestinationAmount = destinations.reduce((total: number, dest: any) => total + parseFloat(dest.purchased_amount || 0), 0);

    if (totalSourceAmount === 0 || totalDestinationAmount === 0) {
      dispatch(showToast({ type: "error", message: "Total amount must be greater than zero." }));
      return;
    }

    if (totalSourceAmount !== totalDestinationAmount) {
      dispatch(showToast({ type: "error", message: "Total Source and Destination amounts must be equal." }));
      return;
    }

    setLoading(true);

    const payload = {
      source_accounts: sources.map((s: any) => ({
        account_id: s.source_account_id,
        account_name: s.source_account_name,
        amount: parseFloat(s.purchased_amount),
        ...(s.receipt_url && {receipt_url: s.receipt_url,}),
        note: s.notes,
      })),
      destination_accounts: destinations.map((d: any) => ({
        account_id: d.destination_account_id,
        account_name: d.destination_account_name,
        amount: parseFloat(d.purchased_amount),
        ...(d.receipt_url && {receipt_url: d.receipt_url,}),
        note: d.notes,
      })),
    };

    const response = await PostAPI("manual-entries/journal-voucher", payload);

    if (!response.error) {
      dispatch(showToast({ type: "success", message: "Transaction added successfully" }));
      dispatch(resetManualState());
      if (isFromJournalVoucher) {
        navigate(`/zoodpay-money/journal-voucher`);
      } else if (selectedBankIdState?.group_type === 'Wallet') {
        navigate(`/zoodpay-money/balance-sheet/wallet`);
      } else if (selectedBankIdState?.group_type === 'Recovery Payable') {
        navigate(`/zoodpay-money/balance-sheet/recovery-payable/transactions`);
      } else if (selectedBankIdState?.group_type === 'Customer Stock GL') {
        navigate(`/zoodpay-money/balance-sheet/customer-stock/transactions`);
      } else if (selectedBankIdState?.group_type === 'Equity/Capital' || selectedBankIdState?.group_type === 'Capital/Owner Equity') {
        navigate(`/zoodpay-money/balance-sheet/equity-capital/transactions`);
      } else if (selectedBankIdState?.group_type === 'Etopup Service Fee Expense') {
        navigate(`/zoodpay-money/balance-sheet/etopup-service-fee-expense/transactions`);
      } else if (selectedBankIdState?.group_type === 'General Expense') {
        navigate(`/zoodpay-money/balance-sheet/general-expense/transactions`);
      } else if (selectedBankIdState?.group_type === 'CVM Commission') {
        navigate(`/zoodpay-money/balance-sheet/cvm-commission/transactions`);
      } else if (selectedBankIdState?.group_type === 'Business Account') {
        navigate(`/zoodpay-money/balance-sheet/business-account/transactions`);
      } else {
        const pathSegment = selectedBankIdState?.group_type?.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-").replace(/\//g, "-");
        navigate(`/zoodpay-money/balance-sheet/${pathSegment}/${selectedBankIdState?.id}`);
      }
    } else {
      dispatch(showToast({ type: "error", message: response?.data?.response?.data?.message }));
    }

    setLoading(false);
  };

  const commonLinks = [
    listOfBalanceSheetUrl ||`/zoodpay-money/balance-sheet`,
    listOfBalanceSheetUrl ||`/zoodpay-money/balance-sheet`,
    listOfBankAccountsUrl || `/zoodpay-money/balance-sheet/bank-accounts`,
    listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/bank-accounts/transactions`,
  ]

  const groupTypeConfig: Record<string, { heading: string; navPath: string[]; links: string[] }> = {
    "Bank Accounts": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Bank Accounts",
        "Bank Account Transactions",
        "Add Manual",
      ],
      links: commonLinks,
    },
    "Cash in Hand": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Cash in Hand",
        "Cash in Hand Transactions",
        "Add Manual",
      ],
      links: commonLinks,
    },
    "Account Receivable (Customers) GL": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Account Receivables (Customers) GL",
        "Account Receivables (Customers) GL Transactions",
        "Add Manual",
      ],
      links: commonLinks,
    },
    "Wallet": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Wallet Transactions",
        "Add Manual",
      ],
      links: [
          listOfBalanceSheetUrl ||`/zoodpay-money/balance-sheet`,
          listOfBalanceSheetUrl ||`/zoodpay-money/balance-sheet`,
          listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/wallet`,
        ],
    },
    "Bank Charges": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Bank Charge Accounts",
        "Bank Charge Transactions",
        "Add Manual",
      ],
      links: commonLinks,
    },
    "Mobile Money": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of Mobile Money",
        "Mobile Money Transactions",
        "Add Manual",
      ],
      links: commonLinks,
    },
    "Recovery Payable": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Recovery Payable Transactions",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/recovery-payable/transactions`,
      ],
    },
    "Customer Stock GL": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Customer Stock GL Transactions",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/customer-stock/transactions`,
      ],
    },
    "Equity/Capital": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Equity/Capital Transactions",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/equity-capital/transactions`,
      ],
    },
    "Capital/Owner Equity": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Capital/Owner Equity Transactions",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/equity-capital/transactions`,
      ],
    },
    "Etopup Service Fee Expense": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Etopup Service Fee Expense Transactions",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/etopup-service-fee-expense/transactions`,
      ],
    },
    "General Expense": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "General Expense Transactions",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/general-expense/transactions`,
      ],
    },
    "CVM Commission": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "List of CVM Commission Transactions",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/cvm-commission/transactions`,
      ],
    },
    "Business Account": {
      heading: "Add Manual",
      navPath: [
        "Zoodpay Money",
        "Balance Sheet",
        "Business Account",
        "Add Manual",
      ],
      links: [
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBalanceSheetUrl || `/zoodpay-money/balance-sheet`,
        listOfBankTransactionsUrl || `/zoodpay-money/balance-sheet/buiness-account/transactions`,
      ],
    },
  };

  const journalVoucherConfig = {
    heading: "Add Manual",
    navPath: [
      "Zoodpay Money",
      "Journal Voucher",
      "Add Manual",
    ],
    links: [
      `/zoodpay-money/journal-voucher`,
      `/zoodpay-money/journal-voucher`,
    ],
  };

  const config = isFromJournalVoucher 
    ? journalVoucherConfig 
    : groupTypeConfig[selectedBankIdState?.group_type || ''] || {
        heading: `Add Manual Transactions`,
        navPath: [
          "Zoodpay Money",
          "Balance Sheet",
          `List of ${selectedBankIdState?.group_type}`,
          `${selectedBankIdState?.group_type} Transactions`,
          "Add Manual",
        ],
        links: commonLinks
      };

  return (
    <div>
      <Breadcrumbs
        navPath={config.navPath}
        heading={config.heading}
        links={config.links}
        extraPadding={true}
      />
      <AddModel>
        <PurchaseTypeDropdown
          isSource={true} 
          setErrors={setErrors}
          errors={errors}
          sourceRefs={sourceRefs}
          selectedOption={'Paid'}
          apiType={'journal-voucher'}
          showPurchasedDate={false}
          showCreditStatus={false}
          showPurchaseType={true}
          maxSources={5}
          maxDestinations={5}
          amountLabel='Amount'
          multipleDestinations={true}
          accounts={sourceAccounts}
          accountsLoading={accountsLoading}
          noteMandatory={true}
          onLoadMoreOptions={onLoadMoreOptionsSource}
        />
        <PurchaseTypeDropdown
          isSource={false}
          setErrors={setErrors}
          errors={errors}
          destinationRefs={destinationRefs}
          selectedOption={'Paid'}
          apiType={'journal-voucher'}
          showPurchasedDate={false}
          showCreditStatus={false}
          showPurchaseType={true}
          maxSources={5}
          maxDestinations={5}
          amountLabel='Amount'
          multipleDestinations={true}
          accounts={destinationAccounts}
          accountsLoading={accountsLoading}
          noteMandatory={true}
          onLoadMoreOptions={onLoadMoreOptionsDestination}
        />
      </AddModel>
      <AddBottomButton buttonName={'Add Manual'}
        onClick={handleSubmit}
        loading={loading}
        buttonWidth="px-4"
        buttonClass="whitespace-nowrap" />
    </div>
  )
}
